import React from 'react';
import { Link } from 'react-router-dom';

const styles = {
  container: {
    backgroundColor: '#ffffff',
    minHeight: '100vh',
    padding: '80px 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontFamily: '"Inter", sans-serif',
    color: '#333',
  },
  contentWrapper: {
    maxWidth: '800px',
    width: '100%',
  },
  // HEADER
  title: {
    fontSize: '3rem',
    fontWeight: '900',
    marginBottom: '20px',
    background: '-webkit-linear-gradient(45deg, #333, #b026ff)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '-1px',
  },
  subtitle: {
    fontSize: '1.2rem',
    lineHeight: '1.6',
    color: '#666',
    marginBottom: '40px',
    borderLeft: '4px solid #b026ff',
    paddingLeft: '20px',
  },
  // SECTIONS
  section: {
    marginBottom: '50px',
  },
  sectionTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    marginBottom: '15px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#333',
  },
  text: {
    fontSize: '1.05rem',
    lineHeight: '1.8',
    color: '#444',
    marginBottom: '20px',
  },
  highlight: {
    color: '#b026ff',
    fontWeight: 'bold',
  },
  // THE BREAKDOWN GRID
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginTop: '30px',
  },
  card: {
    backgroundColor: '#f9f9f9',
    padding: '25px',
    borderRadius: '12px',
    border: '1px solid #eaeaea',
  },
  cardIcon: {
    fontSize: '2rem',
    marginBottom: '10px',
    display: 'block',
  },
  cardTitle: {
    fontWeight: 'bold',
    marginBottom: '5px',
    color: '#333',
  },
  tag: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
};

export default function AIPolicyPage() {
  return (
    <div style={styles.container}>
      <div style={styles.contentWrapper}>
        
        <h1 style={styles.title}>Our Stance on AI</h1>
        
        <p style={styles.subtitle}>
          We believe in transparency. Foreplay Games is a small indie studio with a limited budget 
          but unlimited ambition. To bridge that gap, we use Generative AI as a tool in our workflow.
        </p>

        {/* SECTION 1: THE PHILOSOPHY */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>We get it. Its controversial, especially in the creative space</h2>
          <p style={styles.text}>
            We use AI to assist with asset generation, but the <span style={styles.highlight}>core</span> of our games, from the story, 
            the game design, and especially with the emotional beats, are all 100% human.
          </p>
          <p style={styles.text}>
            We do not use AI to replace creativity, rather we use it to execute our creative vision 
            at a scale that would otherwise be impossible for a small team such as ours to accomplish.
          </p>
        </div>

        {/* SECTION 3: THE GOAL (Your New Section) */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Our Goal</h2>
          <p style={styles.text}>
            While we rely on AI for art in some of our projects, our long-term vision is different.
            Our ultimate goal as a studio is to transition to <span style={styles.highlight}>fully human-created assets</span>.
          </p>
          <p style={styles.text}>
            We ask for your trust. We are not using AI because we are wanting to take a shortcut, but due to the scale of our vision.
            AI simply makes that attainable for us right now. 
            We are well aware of the low-effort content flooding the market (the "AI slop"), and we refuse to be part of it. 
            Instead, we view AI as a <b>stepping stone</b>, and hope to leave it in the dust with time.
          </p>
          <p style={styles.text}>
            Our hope is that our first few projects succeed enough to provide us with the resources to hire 
            real human artists for our future titles, bringing even more soul and unique vision to the worlds we create.
          </p>
        </div>

        {/* SECTION 2: THE BREAKDOWN */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Where We Use It (And Where We Don't)</h2>
          
          <div style={styles.grid}>
            
            {/* ART */}
            <div style={styles.card}>
              <span style={styles.cardIcon}>🎨</span>
              <h3 style={styles.cardTitle}>Visual Arts</h3>
              <div style={{marginBottom: '10px'}}>
                <span style={{...styles.tag, backgroundColor: '#e0c3fc', color: '#5a189a'}}>AI Assisted</span>
              </div>
              <p style={{fontSize: '0.9rem', color: '#666'}}>
                Character sprites and backgrounds are generated using AI tools, then manually 
                retouched and composited by human hands.
              </p>
            </div>

            {/* WRITING */}
            <div style={styles.card}>
              <span style={styles.cardIcon}>✍️</span>
              <h3 style={styles.cardTitle}>Writing & Story</h3>
              <div style={{marginBottom: '10px'}}>
                <span style={{...styles.tag, backgroundColor: '#caffbf', color: '#2b9348'}}>100% Human</span>
              </div>
              <p style={{fontSize: '0.9rem', color: '#666'}}>
                Every line of dialogue, every plot twist, and every character arc is written 
                by a human being. We do not use AI to write scripts.
              </p>
            </div>

            {/* VOICE */}
            <div style={styles.card}>
              <span style={styles.cardIcon}>🎙️</span>
              <h3 style={styles.cardTitle}>Voice Acting</h3>
              <div style={{marginBottom: '10px'}}>
                <span style={{...styles.tag, backgroundColor: '#caffbf', color: '#2b9348'}}>100% Human</span>
              </div>
              <p style={{fontSize: '0.9rem', color: '#666'}}>
                We believe voice actors bring characters to life. We hire real voice talent 
                for our games.
              </p>
            </div>

            {/* CODE */}
            <div style={styles.card}>
              <span style={styles.cardIcon}>💻</span>
              <h3 style={styles.cardTitle}>Code & Engineering</h3>
              <div style={{marginBottom: '10px'}}>
                <span style={{...styles.tag, backgroundColor: '#a0c4ff', color: '#003566'}}>Hybrid</span>
              </div>
              <p style={{fontSize: '0.9rem', color: '#666'}}>
                Core architecture is human-designed. We use AI assistants to speed up boilerplate 
                coding and debugging.
              </p>
            </div>

          </div>
        </div>
        <div style={{marginTop: '40px', textAlign: 'center'}}>
          <Link to="/home" style={{color: '#b026ff', fontWeight: 'bold', textDecoration: 'none', fontSize: '1.1rem'}}>
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}